import {
  Button,
  ContentHeader,
  Css,
  EntityLockup,
  Icon,
  IconButton,
  IconKey,
  Palette,
  useComputed,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperationText } from "src/routes/components/PotentialOperationsUtils";
import { StatTotals } from "src/routes/projects/change-events/guided/components/StatTotals";
import { useSelectOptionsStepQuery } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/1.SelectOptionsStep.generated";
import { AddOrModifyOptionsFormInput } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/addOrModifyOptionsWorkflow";
import { useSaveOptionSelection } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/useSaveOptionSelection";
import { imageUrl } from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import {
  OptionPrice,
  OptionPriceMode,
  optionAmountInCents,
} from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { ReviewImpactedPrice } from "src/routes/projects/change-events/guided/workflows/components/ReviewImpactedPrice";
import {
  hasToggle,
  toSelectOptionsSections,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils";
import {
  SelectOptionsUtils_CheckoutConfigFragment,
  SelectOptionsUtils_OptionGroupFragment,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils.generated";
import { ProjectParams } from "src/routes/routesDef";
import { fail, queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

type SelectedOptionsPaneProps = { formState: ObjectState<AddOrModifyOptionsFormInput> };

/**
 * The right pane's running list of what the draft adds to, removes from and changes in the project's current
 * configuration, with an X per row to undo that change and a link to undo them all.
 *
 * It runs the Select Options step's own query (same `configId` / `structureId` variables, `cache-first`), so Apollo
 * answers it from the normalized `CheckoutConfig` the step already fetched rather than requesting it again. The save
 * mutation returns that same fragment, so every save (the cards' or the pane's own) rewrites that one cache entry and
 * both the step and the pane re-render from it. The selection itself comes from the workflow `formState`, which the
 * shared save hook keeps at the server's latest answer.
 */
export function SelectedOptionsPane(props: SelectedOptionsPaneProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const configId = useComputed(() => formState.configChange.resultId.value, [formState]);
  const query = useSelectOptionsStepQuery({
    variables: { configId: configId ?? fail("Change event has no result config"), structureId: projectId },
    fetchPolicy: "cache-first",
  });
  return queryResult(query, ({ checkoutConfig }) => (
    <SelectedOptionsPaneView checkoutConfig={checkoutConfig} formState={formState} />
  ));
}

type SelectedOptionsPaneViewProps = {
  checkoutConfig: SelectOptionsUtils_CheckoutConfigFragment;
  formState: ObjectState<AddOrModifyOptionsFormInput>;
};

function SelectedOptionsPaneView(props: SelectedOptionsPaneViewProps) {
  const { checkoutConfig, formState } = props;
  const tid = useTestIds(props, "selectedOptions");
  const { openModal, closeModal } = useModal();
  const groups = checkoutConfig.config.optionGroups;
  const { save, saving } = useSaveOptionSelection(formState, groups);
  const optionIds = useComputed(() => formState.configChange.optionIds.value ?? [], [formState]);
  const originalOptionIds = useComputed(() => formState.configChange.originalOptionIds.value ?? [], [formState]);
  const priceMode = useComputed(() => formState.priceMode.value ?? OptionPriceMode.Price, [formState]);
  const isPrice = priceMode === OptionPriceMode.Price;

  const changes = toOptionChanges(groups, optionIds, originalOptionIds, priceMode);
  const changeByRpogId = changes.keyBy((change) => change.group.group.id);
  const sections = toSelectOptionsSections(groups, checkoutConfig.interiorSubSteps)
    .map((section) => ({
      title: section.title,
      changes: section.groups.map((group) => changeByRpogId[group.group.id]).compact(),
    }))
    .filter((section) => section.changes.nonEmpty);

  /**
   * Puts the project's original selection back for the given changes: the current option (and its child variant) goes
   * out, the original one (and its child variant) comes back in, and the rest of the selection is kept. Both sides are
   * what the user "toggled", so a revert that would drop other options gets the same Option Conflict confirmation a
   * card does. A save from here sets `formState.configChange.optionIds`, which re-maps the step's card rows.
   */
  async function revert(reverted: OptionChange[]) {
    const currentIds = reverted.flatMap((change) => selectionIds(change.current));
    const originalIds = reverted.flatMap((change) => selectionIds(change.original));
    await save(optionIds.difference(currentIds).concat(originalIds).unique(), currentIds.concat(originalIds).unique());
  }

  function clearAll() {
    openModal({
      content: (
        <ConfirmationModal
          title="Clear all changes?"
          confirmationMessage="Every option you've added, removed, or changed will go back to what the project has today. You'll need to make those changes again if you still want them."
          label="Clear all"
          danger
          // The revert may open the Option Conflict modal in this one's place, so it closes the modal itself first
          closeOnConfirm={false}
          onConfirmAction={() => {
            closeModal();
            void revert(changes);
          }}
        />
      ),
    });
  }

  return (
    <div css={Css.df.fdc.gap3.$} {...tid}>
      <StatTotals
        testId="selectedOptionsTotals"
        items={[
          {
            // The zeros of the empty state stay plain; the colors are for an actual change
            value: <span css={Css.if(changes.nonEmpty).blue600.$}>{changes.length}</span>,
            label: "Updated",
            testId: "updated",
          },
          {
            value: changes.isEmpty ? (
              "$0"
            ) : (
              <ReviewImpactedPrice
                valueInCents={changes.sum((c) => c.priceDeltaInCents)}
                showZeroAsNone
                hideDisplayDirection={!isPrice}
              />
            ),
            label: priceMode === OptionPriceMode.Cost ? "Cost Δ from Previous" : "$Δ from Previous",
            testId: "delta",
          },
        ]}
      />
      {changes.isEmpty && (
        <div css={Css.sm.gray700.tac.$} {...tid.emptyState}>
          Changes will be populated here when options are modified to the left.
        </div>
      )}
      {sections.map((section) => (
        <div key={section.title} css={Css.df.fdc.gap2.$}>
          {/* Inside the pane the header must not take the page\'s horizontal padding */}
          <ContentHeader title={section.title} level={4} xss={Css.px0.$} />
          {section.changes.map((change) => (
            <ChangeRow
              key={change.group.id}
              change={change}
              displayDirection={isPrice}
              saving={saving}
              onRemove={() => revert([change])}
            />
          ))}
        </div>
      ))}
      {changes.nonEmpty && (
        <div css={Css.df.jcc.$}>
          <Button variant="tertiary" label="Clear all changes" disabled={saving} onClick={clearAll} />
        </div>
      )}
    </div>
  );
}

type ChangeRowProps = { change: OptionChange; displayDirection: boolean; saving: boolean; onRemove: () => void };

/** One changed group: thumbnail, group name, what's selected now, what kind of change it is, its price delta and an X. */
function ChangeRow(props: ChangeRowProps) {
  const { change, displayDirection, saving, onRemove } = props;
  const { group, type, current, original, priceDeltaInCents } = change;
  // A removed group has nothing selected now, so the row describes what the project had
  const shown = current ?? original ?? fail(`Change in group ${group.id} has no selection on either side`);
  const title = group.group.name;
  const subtitle = [shown.option.option.name, shown.childOption?.option.name]
    .compact()
    .filter((name) => name !== title)
    .join(" · ");
  const imgSrc =
    imageUrl(shown.option) ?? (shown.childOption && imageUrl(shown.childOption)) ?? PRODUCT_FALLBACK_IMG_URL;
  // A change the server made on the back of another one, e.g. the exterior scheme it swapped once the draft changed the
  // elevation the project's scheme requires: the original can't come back on its own, only by undoing the change it
  // came with. The option carries the server's reason, the group doesn't.
  const reason = disableBasedOnPotentialOperationText(original?.option.canBeSelected);
  // An option another one turned on, e.g. the range hood style that comes with the decorative hood: a pick-one group
  // can't be left empty and a required group can't be turned off, so there's nothing to undo on this row
  const addedByAnother = type === "Added" && (!hasToggle(group) || !group.canBeSelected.allowed);
  const cannotRevert = !group.canBeSelected.allowed
    ? "This option can no longer be selected on the project"
    : reason &&
      `This changed along with another option, so it can't be undone on its own (${reason}). Undo that option's change to bring the original back.`;

  return (
    <EntityLockup
      compact
      imgSrc={imgSrc}
      title={title}
      description={
        <div css={Css.df.fdc.gapPx(2).$}>
          {subtitle && <div>{subtitle}</div>}
          <div css={Css.df.aic.gapPx(4).$}>
            <Icon icon={changeIcons[type].icon} color={changeIcons[type].color} inc={2} />
            {type}
          </div>
        </div>
      }
      right={
        // The lockup stacks its right slot, so keep the price and the X on one line like the Figma
        <div css={Css.df.aic.gap1.$}>
          {/* Same as the cards: a zero delta reads as "Included" rather than "$0" */}
          <OptionPrice priceInCents={priceDeltaInCents} displayDirection={displayDirection} trim />
          {!addedByAnother && (
            <IconButton icon="x" label="Undo change" compact disabled={cannotRevert || saving} onClick={onRemove} />
          )}
        </div>
      }
    />
  );
}

const changeIcons: Record<OptionChange["type"], { icon: IconKey; color: Palette }> = {
  Added: { icon: "plus", color: Palette.Green600 },
  Removed: { icon: "minus", color: Palette.Red600 },
  Changed: { icon: "refresh", color: Palette.Blue600 },
};

type PotentialOption = SelectOptionsUtils_OptionGroupFragment["options"][number];

/** A group's selection on one side: its option and, for a personalization parent, the variant selected in its child group. */
type Selection = { option: PotentialOption; childOption?: PotentialOption };

type OptionChange = {
  group: SelectOptionsUtils_OptionGroupFragment;
  type: "Added" | "Removed" | "Changed";
  /** The draft's selection in the group, `undefined` when it was removed. */
  current?: Selection;
  /** The project's selection in the group, `undefined` when it was added. */
  original?: Selection;
  /** The current selection's price (or cost, under `OptionPriceMode.Cost`) less the original's. */
  priceDeltaInCents: number;
};

/**
 * One change per top-level group whose selection differs between the draft's `optionIds` and the project's
 * `originalOptionIds`, in the groups' order. A child group's selection (the washer variant under "Add Washer") folds
 * into its parent's change, like the cards, and only counts on a side where the parent is selected.
 *
 * A personalization group the draft has taken out of scope is skipped. The project can hold a selection there that the
 * draft's other options have since superseded, e.g. a tile Kitchen Backsplash next to a Slab Backsplash Upgrade. The
 * backend drops that selection on the draft's first save and won't take it back, so it would show as a "Removed" the
 * user never made and can't undo.
 */
function toOptionChanges(
  groups: SelectOptionsUtils_OptionGroupFragment[],
  optionIds: string[],
  originalOptionIds: string[],
  priceMode: OptionPriceMode,
): OptionChange[] {
  const groupByRpogId = groups.keyBy((group) => group.group.id);
  const currentIds = new Set(optionIds);
  const originalIds = new Set(originalOptionIds);

  function selectionIn(group: SelectOptionsUtils_OptionGroupFragment, ids: Set<string>): Selection | undefined {
    const option = group.options.find((o) => ids.has(o.option.id));
    if (!option) return undefined;
    const childGroup = group.childGroup && groupByRpogId[group.childGroup.id];
    const childOption = childGroup?.options.find((o) => ids.has(o.option.id));
    return { option, childOption };
  }

  return groups
    .filter((group) => !group.parentGroup && !(group.group.personalizationType && !group.canBeSelected.allowed))
    .map((group): OptionChange | undefined => {
      const current = selectionIn(group, currentIds);
      const original = selectionIn(group, originalIds);
      const type = !current && !original ? undefined : !original ? "Added" : !current ? "Removed" : "Changed";
      if (!type || (type === "Changed" && selectionIds(current).xor(selectionIds(original)).isEmpty)) return undefined;
      const priceDeltaInCents = amountOf(current, priceMode) - amountOf(original, priceMode);
      return { group, type, current, original, priceDeltaInCents };
    })
    .compact();
}

/** The option ids a selection holds, i.e. what goes in (or out of) the change event's `optionIds` for it. */
function selectionIds(selection: Selection | undefined): string[] {
  return [selection?.option.option.id, selection?.childOption?.option.id].compact();
}

/** The selection's price or cost, the option's plus its child variant's. */
function amountOf(selection: Selection | undefined, priceMode: OptionPriceMode): number {
  return [selection?.option, selection?.childOption].compact().sum((o) => optionAmountInCents(o, priceMode));
}
